import styled, { css } from 'styled-components'
import PropTypes from 'prop-types'
import { Children, cloneElement, useState, useEffect, useCallback } from 'react'

import { lists } from '@h2/components/mixins'
import { PG90T, PB, WHITE } from '@h2/components/colors'

const selectedStyle = css`
  outline-width: 3px;
  background-color: ${PG90T};
`
const getSelectedStyle = ({ selected }) => (selected ? selectedStyle : null)
const OptionLi = styled.li`
  color: ${PB};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: none;
  outline-color: ${PB};
  outline-style: solid;
  outline-width: 1px;
  border-radius: 5px;
  background-color: ${WHITE};
  transition: all 0.25s ease-in-out;

  &,
  & * {
    cursor: pointer;
  }

  &:hover {
    ${selectedStyle};
  }

  &:focus-visible {
    outline-width: 3px;
    outline-color: blue;
  }

  ${getSelectedStyle};
`
const List = styled.ul`
  ${lists};
  display: flex;
  flex-direction: ${({ align }) => (align === 'vertical' ? 'column' : 'row')};
  gap: ${({ gap }) => gap};

  > ${OptionLi} {
    flex: 1 1 ${({ size }) => 100 / size}%;
    min-width: 0;
    min-height: ${({ height }) => height};
  }
`
const Option = ({ children, value, ...rest }) => {
  return (
    <OptionLi
      tabIndex="0"
      data-value={value}
      data-group-option
      selected={rest.selected}
      {...rest}>
      {children}
    </OptionLi>
  )
}
const OptionGroup = ({
  children,
  value,
  onClick,
  height = 'auto',
  align = 'horizontal',
  gap = 'normal',
  ...rest
}) => {
  const [selected, setSelected] = useState(value ?? null)
  const handleOnClick = useCallback(
    evt => {
      const isValidKeyboard =
        evt.nativeEvent instanceof KeyboardEvent && evt.key === 'Enter'
      const isNonKeyboard = !(evt.nativeEvent instanceof KeyboardEvent)

      if (isValidKeyboard || isNonKeyboard) {
        const targetOutsideOption = Boolean(
          evt.target.querySelector('[data-group-option]')
        )

        if (!targetOutsideOption) {
          let node = evt.target

          while (!node.dataset.groupOption) {
            node = node.parentNode
          }

          setSelected(node.dataset.value)

          if (typeof onClick === 'function') {
            onClick(node.dataset.value, evt)
          }
        }
      }
    },
    [onClick]
  )

  useEffect(() => {
    if (value !== selected) {
      setSelected(value)
    }
  }, [value, selected])

  return (
    <List
      height={height}
      align={align}
      gap={gap}
      size={Children.count(children)}
      onClick={handleOnClick}
      onKeyUp={handleOnClick}
      {...rest}>
      {Children.map(children, item => {
        const props = {}

        if (item.props?.value === selected) {
          props.selected = true
        }

        return cloneElement(item, { ...item.props, ...props })
      })}
    </List>
  )
}

Option.propTypes = {
  /* The value associated with an item */
  value: PropTypes.string.isRequired
}
OptionGroup.propTypes = {
  /** The value associated with the group of items */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Callback when an item in the group is clicked */
  onClick: PropTypes.func,
  /** The height of items within the group */
  height: PropTypes.string,
  /** The alignment of the options within the group */
  align: PropTypes.oneOf(['horizontal', 'vertical']),
  /** The spacing between the items within the group */
  gap: PropTypes.string
}

export { Option, OptionGroup, OptionLi }
